import { useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { uploadProductImage } from '@/lib/uploadImage';
import { ImageCropModal } from '@/components/admin/ImageCropModal';

interface VendorImageUploadProps {
  label: string;
  currentImage?: string;
  onImageChange: (url: string) => void;
  folder?: 'products' | 'profiles';
}

export function VendorImageUpload({
  label,
  currentImage,
  onImageChange,
  folder = 'products'
}: VendorImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async (croppedBlob: Blob) => {
    setIsUploading(true);
    try {
      // Convert blob to file
      const croppedFile = new File(
        [croppedBlob],
        selectedFile?.name || 'image.jpg',
        { type: 'image/jpeg' }
      );

      // Upload to storage
      const result = await uploadProductImage(croppedFile, folder);
      
      onImageChange(result.url);
      setShowCropModal(false);
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      toast.error('Failed to upload: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div>
        <Label>{label}</Label>
        <div className="mt-2 space-y-4">
          {currentImage && (
            <img
              src={currentImage}
              alt="Current"
              className="w-full max-w-sm h-48 object-cover rounded-lg border"
            />
          )}
          
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={isUploading}
              onClick={() => document.getElementById(`file-${label}`)?.click()}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {isUploading ? 'Uploading...' : 'Upload New Image'}
            </Button>
            <input
              id={`file-${label}`}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      </div>

      {imageToCrop && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={() => setShowCropModal(false)}
          imageUrl={imageToCrop}
          onSave={handleCropSave}
          aspectRatio={folder === 'profiles' ? 1 : 16 / 9}
        />
      )}
    </>
  );
}
